// API service for authentication and other API calls

// User data interface for signup
export interface UserData {
  email: string;
  password?: string;
  mobile?: string;
  username?: string;
  name?: string;
}

// Organization data interface for signup
export interface OrganizationData {
  name: string;
  buildingName: string;
  streetName?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  contactNo?: string;
  location?: string;
  email?: string;
  website?: string;
  companyRegNo?: string;
  taxRegNo?: string;
}
export const BASE_URL = `https://dev.foxiomlabs.com/flow/api`;
export const MEDIA_URL = `https://dev.foxiomlabs.com/flow/api/`;
// export const BASE_URL = `http://localhost:4000/api`;
// export const MEDIA_URL = `http://localhost:4000/api/`;

// Global 403 handler function - will be set by the app
let global403Handler: ((message: string) => void) | null = null;

// Function to set the global 403 handler
export const setGlobal403Handler = (handler: (message: string) => void) => {
  global403Handler = handler;
};

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem("manterflow-token");

// Utility to get current ISO time
const getHostTime = () => new Date().toISOString();

// Generic fetch function with error handling
const fetchWithErrorHandling = async (url: string, options: RequestInit) => {
  try {
    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok) {
      // Handle 403 Forbidden response
      if (response.status === 403) {
        const errorMessage = data?.message || "Access forbidden. You don't have permission to access this resource.";
        
        // Call global 403 handler if available
        if (global403Handler) {
          global403Handler(errorMessage);
        }
        
        // Throw a special error for 403
        throw {
          status: 403,
          message: errorMessage,
          isForbiddenError: true,
        };
      }
      
      // Handle 401 Unauthorized response
      if (response.status === 401) {
        // Clear all authentication data from localStorage
        localStorage.removeItem("authToken");
        localStorage.removeItem("manterflow-user");
        localStorage.removeItem("manterflow-organization");
        localStorage.removeItem("manterflow-token");
        localStorage.removeItem("manterflow-user-profile");
        
        // Get the error message
        const errorMessage = data?.message
          ? data.message
          : "Session expired. Please login again.";
        
        // Create a special error object with a flag to show toast
        const authError = {
          status: 401,
          message: errorMessage,
          isAuthError: true,
          showToast: true,
          // Add a delay before redirecting
          redirectDelay: 2000 // 2 seconds
        };
        
        // Throw the enhanced error object
        throw authError;
      }
      throw {
        status: response.status,
        message: data.message || "An error occurred",
      };
    }

    return data;
  } catch (error) {
    // Check if this is our enhanced auth error object
    if (error && typeof error === 'object' && 'showToast' in error && error.showToast) {
      // Import and use toast
      const { toast } = await import('../hooks/use-toast');
      
      // Show toast notification
      toast({
        title: "Authentication Error",
        description: error.message,
        variant: "destructive",
      });
      
      // Redirect after the specified delay
      if ('redirectDelay' in error && typeof error.redirectDelay === 'number') {
        setTimeout(() => {
          // Use replace instead of href to prevent history issues
          window.location.replace("/login");
        }, error.redirectDelay);
      }
    }
    
    // Standard error handling
    if (error instanceof Error) {
      throw { status: 500, message: error.message };
    }
    throw error;
  }
};

// Create headers with authorization token if available
const createAuthHeaders = () => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "x-host-time": getHostTime(),
  };

  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Authentication API calls
export const authApi = {
  // Login user
  async login(email: string, password: string) {
    return fetchWithErrorHandling(`${BASE_URL}/user/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, hostTime: getHostTime() }),
    });
  },

  // Google sign-in
  async googleSignIn(email: string) {
    return fetchWithErrorHandling(`${BASE_URL}/user/google-signin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, hostTime: getHostTime() }),
    });
  },

  // Google sign-up
  async googleSignUp(email: string, name: string) {
    return fetchWithErrorHandling(`${BASE_URL}/user/google-signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, name }),
    });
  },

  // Forgot password
  async forgotPassword(email: string) {
    const frontendUrl = window.location.origin;
    return fetchWithErrorHandling(`${BASE_URL}/user/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, frontendUrl }),
    });
  },

  // Reset password
  async resetPassword(token: string, password: string) {
    return fetchWithErrorHandling(`${BASE_URL}/user/reset-password/${token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
  },

  // Register user and organization
  async signup(userData: UserData, organizationData: OrganizationData) {
    return fetchWithErrorHandling(`${BASE_URL}/user/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user: userData,
        organization: organizationData,
        hostTime: getHostTime(),
      }),
    });
  },
};

// API calls that require authentication
export const authenticatedApi = {
  // Get user profile
  getProfile: async () => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/user/profile`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  // Get organization details
  getOrganization: async (organizationId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(
      `${BASE_URL}/organization/${organizationId}`,
      {
        method: "GET",
        headers: createAuthHeaders(),
      }
    );
  },

  // Update organization details
  updateOrganization: async (
    organizationId: string,
    data: OrganizationData
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(
      `${BASE_URL}/organization/${organizationId}`,
      {
        method: "PUT",
        headers: createAuthHeaders(),
        body: JSON.stringify(data),
      }
    );
  },

  // File upload endpoint
  uploadFiles: async (formData: FormData) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/upload`, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "x-host-time": getHostTime(),
      },
      body: formData,
    });
  },
};

// Division master endpoints
export const divisionApi = {
  getDivisions: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/master/division`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getDivision: async (divisionId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/division/${divisionId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createDivision: async (data: {
    name: string;
    code: string;
    status: boolean;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/division`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateDivision: async (
    divisionId: string,
    data: {
      name: string;
      code: string;
      status: boolean;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/division/${divisionId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteDivision: async (divisionId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/division/${divisionId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};
// Division master endpoints
export const areaApi = {
  getAreas: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/master/area`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getArea: async (areaId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/area/${areaId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createArea: async (data: {
    name: string;
    description: string;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/area`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateArea: async (
    areaId: string,
    data: {
      name: string;
      description: string;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/area/${areaId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteArea: async (areaId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/area/${areaId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};
// Group master endpoints
export const groupApi = {
  getGroups: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/master/group`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getGroup: async (groupId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/group/${groupId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createGroup: async (data: {
    name: string;
    description: string;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/group`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateGroup: async (
    groupId: string,
    data: {
      name: string;
      description: string;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/group/${groupId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteGroup: async (groupId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/group/${groupId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// Zone master endpoints
export const zoneApi = {
  getZones: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/master/zone`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getZone: async (zoneId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/zone/${zoneId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createZone: async (data: {
    name: string;
    code: string;
    status: boolean;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/zone`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateZone: async (
    zoneId: string,
    data: {
      name: string;
      code: string;
      status: boolean;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/zone/${zoneId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteZone: async (zoneId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/zone/${zoneId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// Category master endpoints
export const categoryApi = {
  getCategories: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/master/category`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getCategory: async (categoryId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/category/${categoryId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createCategory: async (data: {
    name: string;
    code: string;
    status: boolean;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/category`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateCategory: async (
    categoryId: string,
    data: {
      name: string;
      code: string;
      status: boolean;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/category/${categoryId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteCategory: async (categoryId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/category/${categoryId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// Account master endpoints
export const accountApi = {
  getAccountsByTypeAndVisibility: async (type: string, visibility: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(
      `${BASE_URL}/master/account?type=${type}&visibility=${visibility}&status=true`,
      {
        method: "GET",
        headers: createAuthHeaders(),
      }
    );
  },

  getAccounts: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    type?: string;
    visibility?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/master/account`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.type) queryParams.append("type", params.type);
      if (params.visibility)
        queryParams.append("visibility", params.visibility);
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getAccount: async (accountId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/account/${accountId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createAccount: async (data: {
    name: string;
    code: string;
    type: string;
    visibility: string;
    openingBalance: number;
    balanceType: string;
    status: boolean;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/account`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateAccount: async (
    accountId: string,
    data: {
      name: string;
      code: string;
      type: string;
      visibility: string;
      openingBalance: number;
      balanceType: string;
      status: boolean;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/account/${accountId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteAccount: async (accountId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/master/account/${accountId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// User management endpoints
export const userApi = {
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    role?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/user`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.role) queryParams.append("role", params.role);
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getUser: async (userId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/user/${userId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createUser: async (data: {
    name: string;
    email: string;
    password: string;
    mobile: string;
    whatsApp?: string;
    fcm?: string;
    image?: string;
    role: string;
    isActive: boolean;
    organizationId: string;
    cashAccountIds?: string[];
    bankAccountIds?: string[];
    categoryIds?: string[];
    zoneIds?: string[];
    divisionIds?: string[];
    areaIds?: string[];
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/user`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateUser: async (
    userId: string,
    data: {
      name: string;
      email: string;
      password?: string;
      mobile: string;
      whatsApp?: string;
      fcm?: string;
      image?: string;
      role: string;
      isActive: boolean;
      organizationId: string;
      cashAccountIds?: string[];
      bankAccountIds?: string[];
      categoryIds?: string[];
      zoneIds?: string[];
      divisionIds?: string[];
      areaIds?: string[];
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/user/${userId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteUser: async (userId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/user/${userId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// Service endpoints
export const serviceApi = {
  getCustomerServices: async (customerId: string) => {
    return fetchWithErrorHandling(`${BASE_URL}/service/customer/${customerId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getServices: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    groupId?: string;
    accountId?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.query) queryParams.append("query", params.query);
    if (params?.groupId) queryParams.append("groupId", params.groupId);
    if (params?.accountId) queryParams.append("accountId", params.accountId);
    if (params?.status) queryParams.append("status", params.status);

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : "";

    return fetchWithErrorHandling(`${BASE_URL}/service${queryString}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getService: async (serviceId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/service/${serviceId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createService: async (data: {
    name: string;
    code: string;
    groupId: string;
    amount: number;
    accountId: string;
    status: boolean;
    organizationId: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/service`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateService: async (
    serviceId: string,
    data: {
      name: string;
      code: string;
      groupId: string;
      amount: number;
      accountId: string;
      status: boolean;
      organizationId: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/service/${serviceId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteService: async (serviceId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/service/${serviceId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// Consumer endpoints
export const consumerApi = {
  // Get next entry number
  getEntryNumber: async () => {
    return fetchWithErrorHandling(`${BASE_URL}/consumer/entry-number`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getConsumers: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    categoryId?: string;
    areaId?: string;
    status?: string;
    zoneId?: string;
    divisionId?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/consumer`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.categoryId)
        queryParams.append("categoryId", params.categoryId);
      if (params.areaId) queryParams.append("areaId", params.areaId);
      if (params.status) queryParams.append("status", params.status);
      if (params.zoneId) queryParams.append("zoneId", params.zoneId);
      if (params.divisionId)
        queryParams.append("divisionId", params.divisionId);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getConsumer: async (consumerId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/consumer/${consumerId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  createConsumer: async (data: {
    entryNumber: string;
    registrationNumber: string;
    firstName: string;
    lastName: string;
    fullName: string;
    categoryId: string;
    areaId: string;
    zoneId: string;
    divisionId: string;
    contactNumber: string;
    openingBalance: number;
    balanceType: string;
    status: string;
    alternativeNumber?: string;
    email?: string;
    photo?: string;
    idProof?: string;
    buildingNumber?: string;
    street?: string;
    city?: string;
    district?: string;
    state?: string;
    country?: string;
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/consumer`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  updateConsumer: async (
    consumerId: string,
    data: {
      entryNumber: string;
      registrationNumber: string;
      firstName: string;
      lastName: string;
      fullName: string;
      categoryId: string;
      areaId: string;
      zoneId: string;
      divisionId: string;
      contactNumber: string;
      openingBalance: number;
      balanceType: string;
      status: string;
      alternativeNumber?: string;
      email?: string;
      photo?: string;
      idProof?: string;
      buildingNumber?: string;
      street?: string;
      city?: string;
      district?: string;
      state?: string;
      country?: string;
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/consumer/${consumerId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  deleteConsumer: async (consumerId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/consumer/${consumerId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
  // Import consumers from Excel
  importExcel: async (formData: FormData) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/consumer/import-excel`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Do not set Content-Type, browser will set it to multipart/form-data with boundary
        "x-host-time": getHostTime(),
      },
      body: formData,
    });
  },
};

// Voucher endpoints
export const voucherApi = {
  // Get next entry number
  getEntryNumber(voucherType: string) {
    const token = getAuthToken();
    return fetchWithErrorHandling(
      `${BASE_URL}/voucher/entry-number?voucherType=${voucherType}`,
      {
        method: "GET",
        headers: createAuthHeaders(),
      }
    );
  },
  getVouchers(params?: {
    page?: number;
    limit?: number;
    query?: string;
    voucherType?: string;
    type?: string;
    fromDate?: string;
    toDate?: string;
    fromAccountId?: string;
    toAccountId?: string;
  }) {
    const token = getAuthToken();
    let url = `${BASE_URL}/voucher?`;

    if (params) {
      if (params.page) url += `page=${params.page}&`;
      if (params.limit) url += `limit=${params.limit}&`;
      if (params.query) url += `query=${params.query}&`;
      if (params.voucherType) url += `voucherType=${params.voucherType}&`;
      if (params.type) url += `type=${params.type}&`;
      if (params.fromDate) url += `fromDate=${params.fromDate}&`;
      if (params.toDate) url += `toDate=${params.toDate}&`;
      if (params.fromAccountId) url += `fromAccountId=${params.fromAccountId}&`;
      if (params.toAccountId) url += `toAccountId=${params.toAccountId}&`;
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getVoucher(id: string) {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/voucher/${id}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  createVoucher(data: {
    voucherType: string;
    type: string;
    date: string;
    voucherNumber: string;
    description: string;
    fromAccountId: string;
    toAccountId: string;
    fromAccountDescription: string;
    toAccountDescription: string;
    amount: number;
  }) {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/voucher`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },
  updateVoucher(
    id: string,
    data: {
      voucherType: string;
      type: string;
      date: string;
      voucherNumber: string;
      description: string;
      fromAccountId: string;
      toAccountId: string;
      fromAccountDescription: string;
      toAccountDescription: string;
      amount: number;
    }
  ) {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/voucher/${id}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },
  deleteVoucher(id: string) {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/voucher/${id}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },
};

// Collection configuration endpoints
export const collectionApi = {
  // Get next entry number for collection
  getEntryNumber: async () => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/collection/entry-number`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  // Get due customers
  getDueCustomers: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    category?: string;
    zone?: string;
    division?: string;
    customer?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/collection-record/due-customer`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("customer", params.query);
      if (params.category) queryParams.append("category", params.category);
      if (params.zone) queryParams.append("zone", params.zone);
      if (params.division) queryParams.append("division", params.division);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  // Get collection configurations with optional filters
  getCollections: async (params?: {
    page?: number;
    limit?: number;
    query?: string;
    categoryId?: string;
    zoneId?: string;
    divisionId?: string;
    serviceId?: string;
    billingCycle?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/collection`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.categoryId)
        queryParams.append("categoryId", params.categoryId);
      if (params.zoneId) queryParams.append("zoneId", params.zoneId);
      if (params.divisionId)
        queryParams.append("divisionId", params.divisionId);
      if (params.serviceId) queryParams.append("serviceId", params.serviceId);
      if (params.billingCycle)
        queryParams.append("billingCycle", params.billingCycle);
      if (params.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  // Get a single collection configuration by ID
  getCollection: async (collectionId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/collection/${collectionId}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  // Create a new collection configuration
  createCollection: async (data: {
    name: string;
    categoryId?: string;
    divisionId?: string;
    zoneId?: string;
    serviceId: string;
    amount: number;
    billingCycle: string;
    startDate: string;
    endDate: string;
    dueDays: number;
    status: string;
    consumerIds: string[];
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/collection`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  // Update an existing collection configuration
  updateCollection: async (
    collectionId: string,
    data: {
      name: string;
      categoryId?: string;
      divisionId?: string;
      zoneId?: string;
      serviceId: string;
      amount: number;
      billingCycle: string;
      startDate: string;
      endDate: string;
      dueDays: number;
      status: string;
      consumerIds: string[];
    }
  ) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/collection/${collectionId}`, {
      method: "PUT",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  // Delete a collection configuration
  deleteCollection: async (collectionId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/collection/${collectionId}`, {
      method: "DELETE",
      headers: createAuthHeaders(),
    });
  },

  // Get consumers based on category, zone, and division
  getConsumersByFilters: async (params: {
    categoryId?: string;
    zoneId?: string;
    divisionId?: string;
    status?: string;
    limit?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/consumer`;

    // Add query parameters if provided
    const queryParams = new URLSearchParams();
    if (params.categoryId) queryParams.append("categoryId", params.categoryId);
    if (params.zoneId) queryParams.append("zoneId", params.zoneId);
    if (params.divisionId) queryParams.append("divisionId", params.divisionId);
    queryParams.append("status", params.status || "active");
    if (params.limit) queryParams.append("limit", params.limit);

    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  // Get due collections for a consumer
  getDueCollections: async (consumerId: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(
      `${BASE_URL}/collection-record/due-collection?consumerId=${consumerId}`,
      {
        method: "GET",
        headers: createAuthHeaders(),
      }
    );
  },

  // Get today's collection statistics
  getTodayCollections: async () => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/collection-record/today`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
};

// Payment endpoints
export const paymentApi = {
  mark: async (data: {
    collections: {
      collectionRecordId: string;
      payAmount: number;
    }[];
    cashAccount?: {
      id: string;
      amount: number;
    };
    bankAccount?: {
      id: string;
      amount: number;
    };
  }) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/payment/mark`, {
      method: "POST",
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  getPayments: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    fromDate?: string;
    toDate?: string;
    accountId?: string;
    paymentMethod?: string;
    collectedById?: string;
    consumerId?: string;
    collectionId?: string;
    status?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/payment`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.status) queryParams.append("status", params.status);
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);
      if (params.accountId) queryParams.append("accountId", params.accountId);
      if (params.collectedById)
        queryParams.append("collectedById", params.collectedById);
      if (params.paymentMethod)
        queryParams.append("paymentMethod", params.paymentMethod);
      if (params.consumerId)
        queryParams.append("consumerId", params.consumerId);
      if (params.collectionId)
        queryParams.append("collectionId", params.collectionId);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },

  getPaymentById: async (id: string) => {
    const token = getAuthToken();
    return fetchWithErrorHandling(`${BASE_URL}/payment/${id}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
};

// Dashboard endpoints
export const dashboardApi = {
  getBasicCount() {
    return fetchWithErrorHandling(`${BASE_URL}/dashboard/basic-count`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getTopCollectors() {
    return fetchWithErrorHandling(`${BASE_URL}/dashboard/top-collectors`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getUpcomingDue() {
    return fetchWithErrorHandling(`${BASE_URL}/dashboard/upcoming-due`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getVoucherDistribution() {
    return fetchWithErrorHandling(`${BASE_URL}/dashboard/voucher-distribution`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getRecentPayments() {
    return fetchWithErrorHandling(`${BASE_URL}/dashboard/recent-payments`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  getCollectionOverTime(period: 'day' | 'week' | 'month' = 'month') {
    return fetchWithErrorHandling(`${BASE_URL}/dashboard/collection-over-time?period=${period}`, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  // Get last six months payment
  getLastSixMonthPayment() {
    return fetchWithErrorHandling(
      `${BASE_URL}/dashboard/last-six-month-payment`,
      {
        method: "GET",
        headers: createAuthHeaders(),
      }
    );
  },
};

// Account log endpoints
export const accountLogApi = {
  getAccountLogs: async (params?: {
    page?: number;
    limit?: number | string;
    search?: string;
    fromDate?: string;
    toDate?: string;
    creditAccount?: string;
    debitAccount?: string;
    accountId?: string;
    type?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/account-log`;

    // Add query parameters if provided
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);
      if (params.type) queryParams.append("type", params.type);
      if (params.accountId) queryParams.append("accountId", params.accountId);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
};

// Activity log endpoints
export const activityLogApi = {
  getActivityLogs: async (params?: {
    page?: number;
    limit?: number | string;
    search?: string;
    fromDate?: string;
    toDate?: string;
    module?: string;
    action?: string;
    userId?: string;
  }) => {
    let url = `${BASE_URL}/activity-log`;
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);
      if (params.module) queryParams.append("module", params.module);
      if (params.action) queryParams.append("action", params.action);
      if (params.userId) queryParams.append("userId", params.userId);
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
};

// Report endpoints
export const reportApi = {
  getCollectionConsumerReport: async (params?: {
    page?: number;
    limit?: number | string;
    query?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/report-collection/consumer`;
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  // New: Service report endpoint
  getCollectionServiceReport: async (params?: {
    page?: number;
    limit?: number | string;
    query?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/report-collection/service`;
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
  // Receivable report endpoint
  getReceivableReport: async (params?: {
    page?: number;
    limit?: number | string;
    query?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const token = getAuthToken();
    let url = `${BASE_URL}/report-recievable/consumer`;
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.query) queryParams.append("query", params.query);
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return fetchWithErrorHandling(url, {
      method: "GET",
      headers: createAuthHeaders(),
    });
  },
};

// Export all API functions
export default {
  auth: authApi,
  authenticated: authenticatedApi,
  division: divisionApi,
  area: areaApi,
  group: groupApi,
  zone: zoneApi,
  category: categoryApi,
  account: accountApi,
  user: userApi,
  service: serviceApi,
  consumer: consumerApi,
  voucher: voucherApi,
  collection: collectionApi,
  payment: paymentApi,
  dashboard: dashboardApi,
  accountLog: accountLogApi,
  activityLog: activityLogApi,
  report: reportApi,
};
